import React, { useCallback, useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Breadcrumbs,
	ButtonBase,
	Checkbox,
	Collapse,
	Dialog,
	Divider,
	Drawer,
	FormControlLabel,
	IconButton,
	Link,
	Menu,
	MenuItem,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import CustomButton from '../components/v2/general/CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../components/v2/general/CustomDataGrid/CustomColumnMenu'
import { dataGridDensityTransitionSx } from '../components/v2/general/CustomDataGrid/dataGridDensityTransitionSx'
import { useAnimatedDensity } from '../components/v2/general/CustomDataGrid/useAnimatedDensity'
import DriversManagementDrawer from '../components/Schedules/DriversManagementDrawer'
import VehiclesManagementDrawer from '../components/Schedules/VehiclesManagementDrawer'
import vehiclesMock from '../data/mockedData/schedules/vehicles.json'
import driversMock from '../data/mockedData/schedules/drivers.json'
import { useModal } from '../store/ModalProvider'
import { useNavigate } from 'react-router-dom'
import type { DriverSituationModalRow, DriverSituationsModalData, DriversWithoutRestRow } from '../types/modal'

type DensityMode = 'compact' | 'standard'

interface Vehicle {
	id: number
	code: string
	seats: number
	status: string
	type: string
	situation: string
	location: string
}

interface Driver {
	id: number
	name: string
	status: string
	situation: string
	location: string
	ateDia?: string
	noturno?: string
}

interface DriverRow {
	id: number
	name: string
	number: string
	status: string
	situation: string
	ateDia: string
	noturno: string
}

interface VehicleRow {
	id: number
	code: string
	type: string
	status: string
	situation: string
	location: string
}

const serviceSummaryFields = [
	{ label: 'Início', value: '02/05/2024 09:00' },
	{ label: 'Local', value: 'Ericeira' },
	{ label: 'Término', value: '02/05/2024 10:00' },
	{ label: 'Local', value: 'Mafra' },
]

const serviceMetaFields = [
	{ label: 'Serviço', value: '0090000' },
	{ label: 'NIF', value: '1234567' },
	{ label: 'Chapa/Requisição', value: '1115_PE2_PNE2' },
]

const serviceBadges = [
	{ label: 'Lotação', value: '51' },
	{ label: 'Carga' },
	{ label: 'Vazio' },
	{ label: 'Vazio Técnico' },
]

const serviceClient = { label: 'Cliente', value: 'Best Transfers 4 U' }
const serviceSummaryRows = [
	[serviceSummaryFields[0], serviceSummaryFields[1]],
	[serviceSummaryFields[2], serviceSummaryFields[3]],
]
const driverAlerts = [
	{
		id: 'task',
		text: 'Tarefa: Ausente por motivo de consulta no dia 02/05/2024 - 15:00 às 17:00.',
		background: 'linear-gradient(90deg, #FF7A00 0%, #FFB45B 100%)',
	},
	{
		id: 'tachograph',
		text: 'Tacógrafo Digital: o Motorista não tem cartão de motorista.',
		background: 'linear-gradient(90deg, #F5B350 0%, #F6C777 100%)',
	},
]

const serviceFieldLabelSx = {
	fontSize: '11px',
	color: '#092056',
	letterSpacing: '0.15px',
	lineHeight: '12px',
}

const serviceFieldPaddingX = 12
const summaryFieldPaddingX = 12
const summaryFieldRowGap = 2
const summaryFieldLabelOffset = 6

const serviceFieldBoxSx = {
	backgroundColor: '#F5F7FC',
	borderRadius: '4px',
	padding: `8px ${serviceFieldPaddingX}px`,
}

const serviceFieldLabelAlignedSx = {
	...serviceFieldLabelSx,
	paddingLeft: `${serviceFieldPaddingX}px`,
	paddingRight: '4px',
	backgroundColor: '#FFFFFF',
	position: 'relative',
	top: `${summaryFieldLabelOffset}px`,
	zIndex: 1,
	width: 'fit-content',
}

const summaryFieldLabelSx = {
	...serviceFieldLabelSx,
	paddingLeft: `${summaryFieldPaddingX}px`,
	paddingRight: '4px',
	backgroundColor: '#FFFFFF',
	position: 'relative',
	top: `${summaryFieldLabelOffset}px`,
	zIndex: 1,
	width: 'fit-content',
}

const summaryFieldBoxSx = {
	...serviceFieldBoxSx,
	padding: `8px ${summaryFieldPaddingX}px`,
}

const serviceFieldValueSx = {
	fontFamily: 'Poppins',
	fontSize: '13px',
	color: '#092056',
	fontWeight: 500,
	lineHeight: 1.3,
}
const itineraryFieldSx = {
	'& .MuiInputLabel-root': {
		fontSize: '11px',
		color: '#092056',
		lineHeight: '12px',
		letterSpacing: '0.15px',
		transformOrigin: 'top left',
		backgroundColor: '#FFFFFF',
		padding: '0 4px',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#F5F7FC',
		borderRadius: '4px',
		minHeight: '33px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'transparent',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
	},
}
const itineraryTableHeaderSx = {
	fontFamily: 'Poppins',
	fontSize: '12px',
	fontWeight: 600,
	lineHeight: '24px',
	letterSpacing: '0.17px',
	color: '#8695AF',
}
const itineraryTableCellSx = {
	fontFamily: 'Poppins',
	fontSize: '12px',
	fontWeight: 400,
	lineHeight: 1.3,
	color: '#092056',
}
const cardSurfaceSx = {
	backgroundColor: '#FFFFFF',
	borderRadius: '12px',
	padding: '16px',
	border: '1px solid #EEF2FA',
	boxShadow: '0 6px 16px rgba(9, 32, 86, 0.06)',
}
const serviceCardTitleSx = {
	fontFamily: 'Poppins',
	fontWeight: 600,
	fontSize: '24px',
	lineHeight: 1.235,
	color: '#092056',
}
const serviceObservationsFieldSx = {
	'& .MuiInputLabel-root': {
		fontSize: '11px',
		color: '#092056',
		lineHeight: '12px',
		letterSpacing: '0.15px',
		transformOrigin: 'top left',
		backgroundColor: '#FFFFFF',
		padding: '0 4px',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '33px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
}
const formFieldSx = {
	'& .MuiInputLabel-root': {
		fontSize: '12px',
		color: '#8695AF',
		lineHeight: 1,
		transformOrigin: 'top left',
		backgroundColor: '#FFFFFF',
		padding: '0 4px',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '6px',
		fontSize: '12px',
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.2)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
}
const selectFieldSx = {
	...formFieldSx,
	'& .MuiOutlinedInput-root': {
		...formFieldSx['& .MuiOutlinedInput-root'],
		backgroundColor: '#FFFFFF',
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}
const checkboxSx = {
	color: 'rgba(134, 149, 175, 0.5)',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
}
const checkboxLabelSx = {
	fontSize: '12px',
	color: '#092056',
}
const serviceCheckboxLabelSx = {
	fontSize: '13px',
	color: '#092056',
	fontWeight: 500,
	lineHeight: 1.3,
}
const actionButtonSx = {
	background: '#00A5FF',
	textTransform: 'none',
	padding: '6px 12px',
	borderRadius: '8px',
	fontSize: '12px',
	fontWeight: 600,
	'&:hover': { background: '#0090E0' },
}
const linkButtonSx = {
	color: '#00A5FF',
	textTransform: 'none',
	padding: 0,
	justifyContent: 'flex-start',
	fontSize: '12px',
	fontWeight: 600,
}
const alertStackSx = {
	display: 'flex',
	flexDirection: 'column',
	gap: '8px',
}
const alertItemSx = {
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	padding: '8px 12px',
	borderRadius: '8px',
	color: '#FFFFFF',
	fontSize: '12px',
	fontWeight: 600,
	lineHeight: 1.25,
}
const alertIconBoxSx = {
	width: 20,
	height: 20,
	borderRadius: '6px',
	backgroundColor: 'rgba(255, 255, 255, 0.25)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
}
const alertTextSx = {
	fontSize: '12px',
	fontWeight: 600,
	color: '#FFFFFF',
}
const breadcrumbLinkSx = {
	fontFamily: 'Poppins',
	fontWeight: 500,
	fontSize: '11px',
	lineHeight: 1.5,
	letterSpacing: '0.15px',
	color: '#8695AF',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'underline',
	},
}
const breadcrumbCurrentSx = {
	fontFamily: 'Poppins',
	fontWeight: 500,
	fontSize: '11px',
	lineHeight: 1.5,
	letterSpacing: '0.15px',
	color: '#00A5FF',
}
const breadcrumbSeparatorSx = {
	fontFamily: 'Poppins',
	fontWeight: 500,
	fontSize: '11px',
	lineHeight: 1.5,
	letterSpacing: '0.15px',
	color: '#8695AF',
}
const tableIconButtonSx = {
	width: 28,
	height: 28,
	border: '1px solid rgba(134, 149, 175, 0.2)',
	backgroundColor: '#F5F7FC',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 0,
}
const tableAssignedIconBoxSx = {
	width: 28,
	height: 28,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
}
const manageDrawerPaperSx = {
	width: { xs: '100%', sm: '354px' },
	backgroundColor: '#FFFFFF',
	borderRadius: 0,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	zIndex: 1600,
	boxShadow:
		'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
}
const manageDrawerHeaderSx = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '28px 24px 0',
	backgroundColor: '#FFFFFF',
	position: 'sticky',
	top: 0,
	zIndex: 1,
}
const manageDrawerTitleSx = {
	fontFamily: 'Poppins',
	fontWeight: 600,
	fontSize: '16px',
	lineHeight: 1.75,
	color: '#8695AF',
	letterSpacing: '0.15px',
}
const manageDrawerCloseButtonSx = {
	width: 36,
	height: 36,
	borderRadius: '20px',
	border: '2px solid rgba(134, 149, 175, 0.15)',
	backgroundColor: '#FFFFFF',
	padding: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	color: '#00A5FF',
	'&:hover': {
		backgroundColor: '#F5F7FC',
	},
}
const manageDrawerSectionSx = {
	display: 'flex',
	flexDirection: 'column',
}
const manageDrawerSectionCompactSx = {
	display: 'flex',
	flexDirection: 'column',
	marginTop: '16px',
}
const manageDrawerSectionTitleSx = {
	fontFamily: 'Poppins',
	fontWeight: 600,
	fontSize: '20px',
	lineHeight: 1.6,
	color: '#092056',
}
const manageDrawerSectionCaretSx = {
	fontSize: '24px',
	color: '#00A5FF',
	transition: 'transform 0.2s ease',
}
const manageDrawerFilterHeaderButtonSx = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	width: '100%',
	padding: 0,
	textAlign: 'left',
}
const manageDrawerFieldSx = {
	'& .MuiInputLabel-root': {
		fontSize: '11px',
		color: '#092056',
		lineHeight: 1,
		transformOrigin: 'top left',
		backgroundColor: '#FFFFFF',
		padding: '0 4px',
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 36px 8px 12px',
		lineHeight: '24px',
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		lineHeight: '24px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '12px',
	},
}
const manageDrawerListSx = {
	display: 'grid',
	gap: 0,
}
const manageDrawerListVehiclesSx = {
	display: 'grid',
	gap: 0,
}
const manageDrawerListItemSx = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	width: '100%',
	minHeight: '48px',
	padding: 0,
	borderRadius: 0,
	borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
	textAlign: 'left',
	'&:hover': {
		backgroundColor: 'transparent',
	},
	'&:last-of-type': {
		borderBottom: 'none',
	},
}
const manageDrawerListItemVehiclesSx = {
	...manageDrawerListItemSx,
}
const manageDrawerListTextSx = {
	fontFamily: 'Poppins',
	fontWeight: 500,
	fontSize: '13px',
	lineHeight: 1.3,
	color: '#092056',
}
const manageDrawerListIconSx = {
	fontSize: '20px',
	color: '#00A5FF',
}
const manageDrawerListIconVehiclesSx = {
	fontSize: '20px',
	color: '#00A5FF',
}
const sampleNote = 'Tristique massa et dui nulli interdum. Et ac nibh placerat justo est enim.'
const itineraryStops = [
	{
		id: '1',
		data: 'seg 02/05/2024',
		horaInicio: '09:00',
		horaFim: '09:29',
		descricao: 'Ericeira Centro',
	},
	{
		id: '2',
		data: 'seg 02/05/2024',
		horaInicio: '09:30',
		horaFim: '09:44',
		descricao: 'Sobreiro',
	},
	{
		id: '3',
		data: 'seg 02/05/2024',
		horaInicio: '09:45',
		horaFim: '10:00',
		descricao: 'Mafra Centro',
	},
]
const manageDriversWithoutRestLabel = 'Motoristas Sem folgas há mais de 6 Dias'
const manageDrawerConsultItemsDrivers = [
	'Mapa de Motoristas',
	manageDriversWithoutRestLabel,
	'Descanso após Folga/Compl. trabalhado',
	'Viaturas e Motoristas Disponíveis',
	'Serviços por Motorista',
	'Serviços por Tipo de Tacógrafo',
	'Declarações de Atividade',
]
const manageDrawerConsultItemsVehicles = [
	'Mapa de Viaturas',
	'Viaturas e Motoristas Disponíveis',
	'Serviços por Viatura',
]
const vehicleMenuItems = ['Ficha da Viatura', 'Serviços e Totais por Viatura']
const driverMenuItems = [
	'Ficha do Motorista',
	'Folha de Ponto Virtual Automática',
	'Situações do Motorista',
	'Serviços e Totais por Motorista',
]

const driverSituationsRows: DriverSituationModalRow[] = [
	{
		id: '1',
		inicio: 'sex 10/05/2024',
		fim: 'sex 10/05/2024',
		dias: '0',
		idSituacao: '3',
		situacao: 'Folga Atrasada',
		descricao: 'Lorem Ipsum',
		idRegisto: '73252',
		idRegistoOk: '000073252',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
	{
		id: '2',
		inicio: 'sáb 01/06/2024',
		fim: 'sex 01/07/2024',
		dias: '28',
		idSituacao: '9',
		situacao: 'Outra Situação',
		descricao: '-',
		idRegisto: '73182',
		idRegistoOk: '000073182',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
	{
		id: '3',
		inicio: 'sáb 02/07/2024',
		fim: 'sex 03/08/2024',
		dias: '31',
		idSituacao: '9',
		situacao: 'Outra Situação',
		descricao: '-',
		idRegisto: '73182',
		idRegistoOk: '000073182',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
]

const SchedulesServicePage = React.memo(function SchedulesServicePage() {
	const vehicles = useMemo(() => vehiclesMock as Vehicle[], [])
	const drivers = useMemo(() => driversMock as Driver[], [])
	const { openModal } = useModal()
	const navigate = useNavigate()

	const [selectedVehicle, setSelectedVehicle] = useState(vehicles[0]?.code ?? '')
	const [selectedDrivers, setSelectedDrivers] = useState<string[]>([drivers[0]?.name ?? ''])
	const [resourcesTab, setResourcesTab] = useState(1)
	const [density, setDensity] = useState<DensityMode>('compact')
	const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<HTMLElement | null>(null)
	const [rowMenuId, setRowMenuId] = useState<number | null>(null)
	const [manageDrawerOpen, setManageDrawerOpen] = useState(false)
	const [manageDriversDrawerOpen, setManageDriversDrawerOpen] = useState(false)
	const [manageVehiclesDrawerOpen, setManageVehiclesDrawerOpen] = useState(false)
	const [itineraryDialogOpen, setItineraryDialogOpen] = useState(false)
	const [manageFiltersOpen, setManageFiltersOpen] = useState(true)
	const [manageConsultOpen, setManageConsultOpen] = useState(true)
	const [driverFilter, setDriverFilter] = useState('Todos')
	const [availabilityFilter, setAvailabilityFilter] = useState('Todos')
	const [selectionFilter, setSelectionFilter] = useState('Casa e Externos')
	const [zoneFilter, setZoneFilter] = useState('Todas')
	const [vehicleFilter, setVehicleFilter] = useState('Todos')
	const [vehicleAvailabilityFilter, setVehicleAvailabilityFilter] = useState('Todos')
	const [vehicleTypeFilter, setVehicleTypeFilter] = useState('Todas')
	const [vehicleCapacityFilter, setVehicleCapacityFilter] = useState('Todas')

	const totalDrivers = 68
	const scheduledDrivers = 30
	const totalVehicles = 85
	const scheduledVehicles = 17

	const canAddSecondDriver = selectedDrivers.length < 2
	const canRemoveSecondDriver = selectedDrivers.length > 1

	const handleDriverChange = (index: number, value: string) => {
		setSelectedDrivers(prev => prev.map((driverName, idx) => (idx === index ? value : driverName)))
	}

	const handleAddDriver = () => {
		setSelectedDrivers(prev => {
			if (prev.length >= 2) {
				return prev
			}
			const nextDriver = drivers.find(driver => !prev.includes(driver.name))?.name ?? drivers[0]?.name ?? ''
			return [...prev, nextDriver]
		})
	}

	const handleRemoveSecondDriver = () => {
		setSelectedDrivers(prev => (prev.length > 1 ? prev.slice(0, 1) : prev))
	}

	const handleAssignDriver = useCallback((driverName: string) => {
		setSelectedDrivers(prev => {
			if (prev.includes(driverName)) {
				return prev
			}
			if (prev.length <= 1) {
				return [driverName]
			}
			return [prev[0], driverName]
		})
	}, [])

	const handleOpenManageDrawer = () => {
		setManageDrawerOpen(false)
		if (resourcesTab === 1) {
			setManageVehiclesDrawerOpen(false)
			setManageDriversDrawerOpen(true)
			return
		}
		setManageDriversDrawerOpen(false)
		setManageVehiclesDrawerOpen(true)
	}

	const handleOpenRowMenu = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
		event.stopPropagation()
		setRowMenuAnchorEl(event.currentTarget)
		setRowMenuId(rowId)
	}

	const handleCloseRowMenu = () => {
		setRowMenuAnchorEl(null)
		setRowMenuId(null)
	}

	const handleRowMenuAction = (action: string) => {
		if (resourcesTab === 1 && action === 'Ficha do Motorista') {
			if (rowMenuId !== null) {
				navigate(`/schedules/driver/${rowMenuId}`)
			}
			handleCloseRowMenu()
			return
		}

		if (resourcesTab === 0 && action === 'Ficha da Viatura') {
			if (rowMenuId !== null) {
				navigate(`/schedules/vehicle/${rowMenuId}`)
			}
			handleCloseRowMenu()
			return
		}

		if (resourcesTab === 1 && action === 'Situações do Motorista') {
			const selectedDriver = drivers.find(item => item.id === rowMenuId) ?? drivers[0]
			const modalData: DriverSituationsModalData = {
				driverId: selectedDriver?.id,
				driverName: selectedDriver?.name ?? 'Z-Aldina',
				startDate: '01/05/2024',
				endDate: '01/09/2024',
				situacao: 'Todas',
				folgaAtual: 'Segunda-Feira',
				complementarAtual: 'Terça-Feira',
				rows: driverSituationsRows,
			}

			openModal('Situações do Motorista', modalData, [])
			handleCloseRowMenu()
			return
		}

		console.log('Menu ação:', action, rowMenuId)
		handleCloseRowMenu()
	}

	const handleOpenSameChapaModal = () => {
		const chapaValue = serviceMetaFields[2]?.value ?? ''
		const serviceId = serviceMetaFields[0]?.value ?? ''

		openModal(
			'Escalar outros serviços da mesma chapa / requisição',
			{
				serviceId,
				chapa: chapaValue,
				motorista: selectedDrivers[0] ?? '',
				secondMotorista: selectedDrivers[1] ?? '2º Motorista',
				viatura: selectedVehicle,
			},
			[],
		)
	}

	const handleOpenItinerary = () => {
		setItineraryDialogOpen(true)
	}

	const handleCloseItinerary = () => {
		setItineraryDialogOpen(false)
	}

	const handleCloseManageDrawer = () => {
		setManageDrawerOpen(false)
	}

	const handleCloseManageDriversDrawer = () => {
		setManageDriversDrawerOpen(false)
	}

	const handleCloseManageVehiclesDrawer = () => {
		setManageVehiclesDrawerOpen(false)
	}

	const driverRows = useMemo<DriverRow[]>(
		() =>
			drivers.map((driver, index) => ({
				id: driver.id,
				name: driver.name,
				number: String(10280 + index),
				status: driver.status,
				situation: driver.situation,
				ateDia: driver.ateDia ?? '16/05/2024',
				noturno: driver.noturno ?? '15/05/2024',
			})),
		[drivers],
	)

	const driversWithoutRestRows = useMemo<DriversWithoutRestRow[]>(
		() =>
			driverRows.slice(0, 6).map((driver, index) => ({
				id: `no-rest-${driver.id}`,
				motorista: driver.name,
				nr: driver.number,
				ultimaFolga: driver.ateDia,
				diasSemFolga: 7 + index,
				estado: driver.situation,
			})),
		[driverRows],
	)

	const vehicleRows = useMemo<VehicleRow[]>(
		() =>
			vehicles.map(vehicle => ({
				id: vehicle.id,
				code: vehicle.code,
				type: vehicle.type,
				status: vehicle.status,
				situation: vehicle.situation,
				location: vehicle.location,
			})),
		[vehicles],
	)

	const handleConsultItemClick = useCallback(
		(item: string) => {
			if (item === 'Serviços por Tipo de Tacógrafo') {
				openModal(
					'Serviços por Tipo de Tacógrafo',
					{
						rows: [],
					},
					[],
				)
				return
			}
			if (item !== manageDriversWithoutRestLabel) {
				return
			}

			openModal(
				'Motoristas sem folga ha mais de 6 dias',
				{
					rows: driversWithoutRestRows,
					thresholdDays: 6,
				},
				[],
			)
		},
		[openModal, driversWithoutRestRows],
	)

	const driverColumns = useMemo<GridColDef[]>(
		() => [
			{
				field: 'name',
				headerName: 'Nome',
				minWidth: 200,
				flex: 1.4,
				cellClassName: 'name-cell',
				renderCell: params => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Avatar
							sx={{
								borderRadius: '100%',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								bgcolor: 'lightgrey 50% / cover no-repeat',
								width: 32,
								height: 32,
								fontSize: '20px',
								fontWeight: 400,
								lineHeight: 1,
							}}
						>
							{params.row.name?.[0] ?? 'M'}
						</Avatar>
						<Typography sx={{ fontSize: '13px' }}>{params.row.name}</Typography>
					</Box>
				),
			},
			{ field: 'number', headerName: 'Nr', width: 80 },
			{ field: 'situation', headerName: 'Situação', minWidth: 140, flex: 1 },
			{ field: 'ateDia', headerName: 'Até Dia', minWidth: 110 },
			{ field: 'noturno', headerName: 'Noturno', minWidth: 110 },
			{
				field: 'assign',
				headerName: '',
				width: 84,
				minWidth: 84,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				headerClassName: 'assign-cell',
				cellClassName: 'assign-cell',
				align: 'right',
				headerAlign: 'right',
				renderCell: params => {
					const driverName = String(params.row.name ?? '')
					const isAssigned = selectedDrivers.includes(driverName)

					return (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								alignItems: 'center',
								width: '100%',
								gap: '6px',
								height: '100%',
							}}
						>
							{isAssigned ? (
								<Box sx={tableAssignedIconBoxSx}>
									<CheckCircleRoundedIcon sx={{ fontSize: '18px', color: '#2CB34A', display: 'block' }} />
								</Box>
							) : (
								<Tooltip title='Escalar nos servicos selecionados'>
									<IconButton
										size='small'
										onClick={event => {
											event.stopPropagation()
											handleAssignDriver(driverName)
										}}
										sx={tableIconButtonSx}
									>
										<ContentPasteOutlinedIcon sx={{ fontSize: '16px', color: '#00A5FF', display: 'block' }} />
									</IconButton>
								</Tooltip>
							)}
							<IconButton
								size='small'
								sx={tableIconButtonSx}
								onClick={event => handleOpenRowMenu(event, Number(params.id))}
							>
								<MoreVertIcon sx={{ fontSize: '16px', color: '#00A5FF', display: 'block' }} />
							</IconButton>
						</Box>
					)
				},
			},
		],
		[handleAssignDriver, selectedDrivers],
	)

	const vehicleColumns = useMemo<GridColDef[]>(
		() => [
			{ field: 'code', headerName: 'Viatura', minWidth: 120, flex: 1 },
			{ field: 'type', headerName: 'Tipo', minWidth: 120, flex: 1 },
			{ field: 'situation', headerName: 'Situação', minWidth: 140, flex: 1 },
			{ field: 'status', headerName: 'Estado', minWidth: 120, flex: 1 },
			{ field: 'location', headerName: 'Local', minWidth: 120, flex: 1 },
		],
		[],
	)

	const targetRowHeight = density === 'compact' ? 36 : 46
	const targetHeaderHeight = density === 'compact' ? 36 : 44
	const cellPadding = density === 'compact' ? '2px 4px' : '4px 4px'
	const { rowHeight, headerHeight } = useAnimatedDensity(targetRowHeight, targetHeaderHeight)

	const panelTitle =
		resourcesTab === 0
			? `${scheduledVehicles}/${totalVehicles} Escaladas`
			: `${scheduledDrivers}/${totalDrivers} Escalados`

	const manageLabel = resourcesTab === 0 ? 'Gerir Viaturas' : 'Gerir Motoristas'
	const rowMenuItems = resourcesTab === 0 ? vehicleMenuItems : driverMenuItems

	return (
		<>
			<Box
				sx={{
					position: 'relative',
					display: 'flex',
					flexDirection: 'column',
					width: '100%',
					height: '100%',
					overflow: 'hidden',
					flex: 1,
					minWidth: 0,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
						width: '100%',
						marginBottom: '12px',
					}}
				>
					<Breadcrumbs
						aria-label='breadcrumb'
						separator={<Typography sx={breadcrumbSeparatorSx}>/</Typography>}
						sx={{
							width: '100%',
							margin: 0,
							'& .MuiBreadcrumbs-ol': {
								alignItems: 'center',
							},
							'& .MuiBreadcrumbs-separator': {
								marginX: '8px',
								marginY: 0,
							},
						}}
					>
						<Link
							underline='none'
							href='/'
							sx={breadcrumbLinkSx}
						>
							Home
						</Link>
						<Link
							underline='none'
							href='/schedules'
							sx={breadcrumbLinkSx}
						>
							Escalas
						</Link>
						<Typography sx={breadcrumbCurrentSx}>Escalar Serviço</Typography>
					</Breadcrumbs>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Poppins',
								fontWeight: 600,
								fontSize: '28px',
								color: '#092056',
							}}
						>
							Escalar Serviço
						</Typography>
						<Box sx={{ display: 'flex', gap: '12px' }}>
							<CustomButton
								sx={{
									background: 'linear-gradient(180deg, #00B2FF 0%, #0090E0 100%)',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '8px',
									boxShadow: '0 6px 14px rgba(0, 165, 255, 0.25)',
									'&:hover': {
										background: 'linear-gradient(180deg, #00A5FF 0%, #0087D6 100%)',
									},
								}}
								onClick={handleOpenSameChapaModal}
							>
								Escalar serviços da mesma chapa
							</CustomButton>
							<CustomButton
								sx={{
									background: '#092056',
									color: '#FFFFFF',
									textTransform: 'none',
									borderRadius: '8px',
									boxShadow: '0 6px 14px rgba(9, 32, 86, 0.25)',
									'&:hover': {
										background: '#08204F',
									},
								}}
							>
								Guardar
							</CustomButton>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
						gap: '20px',
						overflow: 'hidden',
						flex: 1,
						minHeight: 0,
						width: '100%',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
							overflowY: 'auto',
							minHeight: 0,
						}}
					>
						<Box
							sx={{
								...cardSurfaceSx,
								borderRadius: '10px',
								padding: '24px 16px',
								border: 'none',
								boxShadow: 'none',
								display: 'flex',
								flexDirection: 'column',
								gap: '16px',
							}}
						>
							<Typography sx={serviceCardTitleSx}>Serviço</Typography>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '16px',
								}}
							>
								{serviceSummaryRows.map((row, rowIndex) => (
									<Box
										key={`summary-row-${rowIndex}`}
										sx={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: '16px',
											alignItems: 'center',
										}}
									>
										{row.map((field, fieldIndex) => (
											<Box
												key={`${field.label}-${fieldIndex}`}
												sx={{
													display: 'flex',
													flexDirection: 'column',
													gap: `${summaryFieldRowGap}px`,
													flex: fieldIndex === 0 ? '0 0 171px' : '1 1 0',
													minWidth: fieldIndex === 0 ? '171px' : '160px',
												}}
											>
												<Typography sx={summaryFieldLabelSx}>{field.label}</Typography>
												<Box sx={summaryFieldBoxSx}>
													<Typography sx={serviceFieldValueSx}>{field.value}</Typography>
												</Box>
											</Box>
										))}
									</Box>
								))}
							</Box>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: '16px',
									alignItems: 'center',
									width: '100%',
								}}
							>
								<CustomButton
									size='small'
									onClick={handleOpenItinerary}
									sx={{
										background: '#00A5FF',
										textTransform: 'none',
										padding: '4px 10px',
										borderRadius: '4px',
										fontSize: '14px',
										fontWeight: 600,
										lineHeight: '22px',
										width: '171px',
										minHeight: '32px',
										'&:hover': {
											background: '#0090E0',
										},
									}}
								>
									Itinerário
								</CustomButton>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: `${summaryFieldRowGap}px`,
										width: '73px',
									}}
								>
									<Typography sx={serviceFieldLabelAlignedSx}>{serviceBadges[0].label}</Typography>
									<Box sx={serviceFieldBoxSx}>
										<Typography sx={serviceFieldValueSx}>{serviceBadges[0].value}</Typography>
									</Box>
								</Box>
								<Box sx={{ width: '73px' }}>
									<Box sx={serviceFieldBoxSx}>
										<Typography sx={serviceFieldValueSx}>{serviceBadges[1].label}</Typography>
									</Box>
								</Box>
								<Box sx={{ width: '73px' }}>
									<Box sx={serviceFieldBoxSx}>
										<Typography sx={serviceFieldValueSx}>{serviceBadges[2].label}</Typography>
									</Box>
								</Box>
								<Box sx={{ flex: '1 1 0', minWidth: '120px' }}>
									<Box sx={serviceFieldBoxSx}>
										<Typography sx={serviceFieldValueSx}>{serviceBadges[3].label}</Typography>
									</Box>
								</Box>
							</Box>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
								<Box>
									<Typography sx={serviceFieldLabelAlignedSx}>Observações do Comercial</Typography>
									<Box sx={{ ...serviceFieldBoxSx, marginTop: `${summaryFieldRowGap}px` }}>
										<Typography sx={serviceFieldValueSx}>{sampleNote}</Typography>
									</Box>
								</Box>
							</Box>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
									gap: '16px',
								}}
							>
								{serviceMetaFields.map(field => (
									<Box
										key={field.label}
										sx={{ display: 'flex', flexDirection: 'column', gap: `${summaryFieldRowGap}px` }}
									>
										<Typography sx={serviceFieldLabelAlignedSx}>{field.label}</Typography>
										<Box sx={serviceFieldBoxSx}>
											<Typography sx={serviceFieldValueSx}>{field.value}</Typography>
										</Box>
									</Box>
								))}
							</Box>
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: `${summaryFieldRowGap}px` }}>
								<TextField
									label='Observações'
									multiline
									minRows={1}
									maxRows={1}
									size='small'
									defaultValue={sampleNote}
									fullWidth
									sx={serviceObservationsFieldSx}
								/>
							</Box>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										sx={checkboxSx}
									/>
								}
								label={
									<Typography sx={serviceCheckboxLabelSx}>
										Serviço Escalado (motorista deslocou-se mas não efetuou o Serviço)
									</Typography>
								}
							/>
						</Box>

						<Box
							sx={{
								...cardSurfaceSx,
								display: 'flex',
								flexDirection: 'column',
								gap: '12px',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '16px',
									color: '#092056',
								}}
							>
								Viatura e Motorista
							</Typography>
							<Divider sx={{ borderColor: 'rgba(134, 149, 175, 0.2)' }} />
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
								<TextField
									select
									label='Viatura'
									size='small'
									value={selectedVehicle}
									onChange={event => setSelectedVehicle(event.target.value)}
									fullWidth
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={selectFieldSx}
								>
									{vehicles.map(vehicle => (
										<MenuItem
											key={vehicle.id}
											value={vehicle.code}
										>
											{vehicle.code} - {vehicle.type}
										</MenuItem>
									))}
								</TextField>

								<TextField
									label='Comentários à Viatura'
									multiline
									minRows={2}
									size='small'
									defaultValue={sampleNote}
									fullWidth
									sx={formFieldSx}
								/>

								{selectedDrivers.map((driverName, index) => (
									<Box
										key={`driver-${index}`}
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '16px',
										}}
									>
										<Box
											sx={{
												display: 'grid',
												gridTemplateColumns: 'minmax(0, 1fr) auto',
												gap: '12px',
												alignItems: 'flex-start',
											}}
										>
											<TextField
												select
												label={index === 0 ? 'Motorista' : 'Motorista 2'}
												size='small'
												value={driverName}
												onChange={event => handleDriverChange(index, event.target.value)}
												fullWidth
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={selectFieldSx}
											>
												{drivers.map(driver => (
													<MenuItem
														key={driver.id}
														value={driver.name}
													>
														{driver.name}
													</MenuItem>
												))}
											</TextField>
											<Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
												<CustomButton
													size='small'
													sx={actionButtonSx}
												>
													Folha de Ponto
												</CustomButton>
												<CustomButton
													size='small'
													sx={actionButtonSx}
												>
													Horário Motorista
												</CustomButton>
											</Box>
										</Box>

										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
											<FormControlLabel
												control={
													<Checkbox
														size='small'
														sx={checkboxSx}
													/>
												}
												label={<Typography sx={checkboxLabelSx}>Agente Único</Typography>}
											/>
											<FormControlLabel
												control={
													<Checkbox
														size='small'
														sx={checkboxSx}
													/>
												}
												label={<Typography sx={checkboxLabelSx}>Desdobramento</Typography>}
											/>
											<FormControlLabel
												control={
													<Checkbox
														size='small'
														sx={checkboxSx}
													/>
												}
												label={<Typography sx={checkboxLabelSx}>Escalado em Descanso</Typography>}
											/>
										</Box>

										<TextField
											label='Comentários ao Motorista'
											multiline
											minRows={3}
											size='small'
											defaultValue={sampleNote}
											fullWidth
											sx={formFieldSx}
										/>
									</Box>
								))}

								<Box sx={alertStackSx}>
									{driverAlerts.map(alert => (
										<Box
											key={alert.id}
											sx={{ ...alertItemSx, background: alert.background }}
										>
											<Box sx={alertIconBoxSx}>
												<WarningAmberRoundedIcon sx={{ fontSize: '16px', color: '#FFFFFF' }} />
											</Box>
											<Typography sx={alertTextSx}>{alert.text}</Typography>
										</Box>
									))}
								</Box>

								{canAddSecondDriver ? (
									<CustomButton
										variant='text'
										sx={linkButtonSx}
										onClick={handleAddDriver}
									>
										Adicionar 2º Motorista +
									</CustomButton>
								) : (
									<CustomButton
										variant='text'
										sx={linkButtonSx}
										onClick={handleRemoveSecondDriver}
										disabled={!canRemoveSecondDriver}
									>
										Remover Motorista 2
									</CustomButton>
								)}
							</Box>
						</Box>
					</Box>

					<Box
						sx={{
							backgroundColor: 'background.paper',
							borderRadius: '12px',
							padding: '16px',
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						<Tabs
							value={resourcesTab}
							onChange={(_, value) => setResourcesTab(value)}
							TabIndicatorProps={{
								style: {
									backgroundColor: '#00A5FF',
									height: '3px',
									borderRadius: '2px',
								},
							}}
							sx={{
								minHeight: '40px',
								borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
								display: 'flex',
								justifyContent: 'center',
								'& .MuiTab-root': {
									fontSize: '14px',
									color: '#8695AF',
									fontWeight: 600,
									textTransform: 'none',
									minHeight: '40px',
									padding: '6px 12px',
									'&.Mui-selected': { color: '#00A5FF' },
								},
								'& .MuiTabs-flexContainer': {
									justifyContent: 'center',
								},
							}}
						>
							<Tab label='Viaturas' />
							<Tab label='Motoristas' />
						</Tabs>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								padding: '12px 0',
								borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '14px',
									color: '#092056',
								}}
							>
								{panelTitle}
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<CustomButton
									variant='text'
									size='small'
									sx={{
										color: '#00A5FF',
										fontWeight: 600,
										textTransform: 'none',
										padding: '4px 6px',
									}}
									onClick={handleOpenManageDrawer}
								>
									{manageLabel}
								</CustomButton>
								<IconButton
									size='small'
									sx={{
										width: 28,
										height: 28,
										border: '1px solid rgba(134, 149, 175, 0.2)',
										backgroundColor: '#FFFFFF',
										'&:hover': {
											backgroundColor: '#F5F7FC',
										},
									}}
								>
									<FilterAltOutlinedIcon sx={{ color: '#00A5FF' }} />
								</IconButton>
								<ToggleButtonGroup
									value={density}
									exclusive
									onChange={(_, value) => value && setDensity(value)}
									size='small'
									sx={{
										border: '1px solid rgba(134, 149, 175, 0.2)',
										borderRadius: '8px',
										backgroundColor: '#FFFFFF',
										'& .MuiToggleButton-root': {
											border: 'none',
											borderRadius: 0,
											padding: '4px 6px',
											'&:not(:last-child)': {
												borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
											},
											'&.Mui-selected': {
												backgroundColor: '#F5F7FC',
												'&:hover': { backgroundColor: '#F5F7FC' },
											},
										},
										'& .MuiToggleButton-root:first-of-type': {
											borderTopLeftRadius: '8px',
											borderBottomLeftRadius: '8px',
										},
										'& .MuiToggleButton-root:last-of-type': {
											borderTopRightRadius: '8px',
											borderBottomRightRadius: '8px',
										},
									}}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Box sx={{ flex: 1, minHeight: 0, marginTop: '12px' }}>
							<DataGrid
								rows={resourcesTab === 0 ? vehicleRows : driverRows}
								columns={resourcesTab === 0 ? vehicleColumns : driverColumns}
								rowHeight={rowHeight}
								columnHeaderHeight={headerHeight}
								disableRowSelectionOnClick
								hideFooter
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{
									basePopper: {
										sx: columnMenuPopperSx,
									},
								}}
								getRowClassName={params => {
									const status = String(params.row.status ?? '')

									if (status.startsWith('Escalad')) {
										return 'row--escalado'
									}
									if (params.row.situation === 'Folga Trabalhada') {
										return 'row--folga'
									}
									return ''
								}}
								sx={[
									{
										'--density-row-height': `${rowHeight}px`,
										'--density-header-height': `${headerHeight}px`,
										border: 'none',
										'--DataGrid-rowBorderColor': 'transparent',
										'& .MuiDataGrid-columnHeaders': {
											backgroundColor: '#FFFFFF',
											borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
											borderTop: 'none',
											boxShadow: 'none',
										},
										'& .MuiDataGrid-columnHeaderTitle': {
											fontFamily: 'Poppins',
											fontWeight: 600,
											fontSize: '12px',
											color: '#8695AF',
											textTransform: 'uppercase',
										},
										'& .MuiDataGrid-columnSeparator': {
											display: 'none',
										},
										'& .MuiDataGrid-columnHeader': {
											borderRight: 'none',
										},
										'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
											padding: cellPadding,
										},
										'& .MuiDataGrid-cell': {
											fontFamily: 'Poppins',
											fontSize: '13px',
											color: '#092056',
											borderBottom: 'none',
											borderRight: 'none',
										},
										'& .MuiDataGrid-cell.assign-cell': {
											paddingLeft: '4px',
											paddingRight: '4px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'flex-end',
										},
										'& .MuiDataGrid-cell.name-cell': {
											display: 'flex',
											alignItems: 'center',
										},
										'& .MuiDataGrid-columnHeader.assign-cell': {
											paddingLeft: '4px',
											paddingRight: '4px',
										},
										'& .MuiDataGrid-row': {
											borderBottom: 'none',
										},
										'& .MuiDataGrid-row:hover': {
											backgroundColor: 'rgba(0, 165, 255, 0.06)',
										},
										'& .row--escalado': {
											backgroundColor: 'rgba(0, 165, 255, 0.08)',
										},
										'& .row--folga': {
											backgroundColor: 'rgba(255, 193, 7, 0.16)',
										},
									},
									dataGridDensityTransitionSx,
								]}
							/>
						</Box>
						<Menu
							anchorEl={rowMenuAnchorEl}
							open={Boolean(rowMenuAnchorEl)}
							onClose={handleCloseRowMenu}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							PaperProps={{
								sx: {
									backgroundColor: '#FFFFFF',
									borderRadius: '4px',
									boxShadow:
										'0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
									minWidth: '236px',
								},
							}}
							MenuListProps={{
								sx: {
									padding: '8px 0',
								},
							}}
						>
							{rowMenuItems.map(item => (
								<MenuItem
									key={item}
									onClick={() => handleRowMenuAction(item)}
									sx={{
										padding: '6px 16px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: '16px',
									}}
								>
									<Typography
										sx={{
											fontFamily: 'Poppins',
											fontWeight: 500,
											fontSize: '12px',
											lineHeight: 1.5,
											color: '#092056',
										}}
									>
										{item}
									</Typography>
									<KeyboardArrowRightRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
								</MenuItem>
							))}
						</Menu>
					</Box>
				</Box>
			</Box>

			<Dialog
				open={itineraryDialogOpen}
				onClose={handleCloseItinerary}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: 'calc(100% - 64px)',
						maxWidth: '1200px',
						borderRadius: '4px',
						boxShadow:
							'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
					},
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'Poppins',
								fontWeight: 600,
								fontSize: '16px',
								lineHeight: 1.75,
								letterSpacing: '0.15px',
								color: '#092056',
							}}
						>
							Requisição do Serviço
						</Typography>
					</Box>

					<Box sx={{ padding: '8px 24px 24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: '#092056',
								}}
							>
								Itinerário do Serviço
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '20px',
									lineHeight: 1.3,
									color: '#092056',
								}}
							>
								Requisição
							</Typography>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
									gap: '16px',
								}}
							>
								<TextField
									select
									label='Tipo de Serviço'
									size='small'
									value='Agência de Viagens'
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={itineraryFieldSx}
								>
									<MenuItem value='Agência de Viagens'>Agência de Viagens</MenuItem>
								</TextField>
								<TextField
									label='Chapa/Requisição'
									size='small'
									value='1115_PE2_PNE2'
									sx={itineraryFieldSx}
								/>
								<TextField
									label='Início'
									size='small'
									value='02/05/2024 09:00'
									sx={itineraryFieldSx}
								/>
								<TextField
									label='Término'
									size='small'
									value='02/05/2024 10:00'
									sx={itineraryFieldSx}
								/>
							</Box>
							<TextField
								label='Observações'
								size='small'
								value="It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English."
								multiline
								minRows={2}
								sx={itineraryFieldSx}
							/>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '20px',
									lineHeight: 1.3,
									color: '#092056',
								}}
							>
								Cliente
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<TextField
									label='Nr Cliente'
									size='small'
									value='611'
									sx={{ ...itineraryFieldSx, minWidth: '171px' }}
								/>
								<TextField
									label='Nome'
									size='small'
									value='Best Transfers 4 U'
									sx={{ ...itineraryFieldSx, flex: '1 1 240px', minWidth: '240px' }}
								/>
								<TextField
									label='Morada'
									size='small'
									value='Ctro.Escrit.Panoramic-Av.Atlantico 16 esq 07.09'
									sx={{ ...itineraryFieldSx, flex: '1 1 360px', minWidth: '260px' }}
								/>
								<TextField
									label='Código Postal'
									size='small'
									value='1990-019'
									sx={{ ...itineraryFieldSx, minWidth: '113px' }}
								/>
								<TextField
									label='Local'
									size='small'
									value='Lisboa'
									sx={{ ...itineraryFieldSx, minWidth: '160px' }}
								/>
								<TextField
									label='Telefone'
									size='small'
									value='217707280'
									sx={{ ...itineraryFieldSx, minWidth: '140px' }}
								/>
								<TextField
									label='Observações do Comercial'
									size='small'
									value={sampleNote}
									multiline
									minRows={2}
									sx={{ ...itineraryFieldSx, flex: '1 1 100%' }}
								/>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '20px',
									lineHeight: 1.3,
									color: '#092056',
								}}
							>
								Itinerário
							</Typography>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<TextField
									label='Descrição'
									size='small'
									value='Ericeira - Mafra'
									sx={{ ...itineraryFieldSx, flex: '1 1 320px', minWidth: '260px' }}
								/>
								<TextField
									label='Finalidade'
									size='small'
									value='Circuito de Turismo'
									sx={{ ...itineraryFieldSx, minWidth: '220px' }}
								/>
								<TextField
									label='Observações do Itinerário'
									size='small'
									value="It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English."
									multiline
									minRows={2}
									sx={{ ...itineraryFieldSx, flex: '1 1 100%' }}
								/>
							</Box>

							<Box
								sx={{
									borderTop: '1px solid rgba(134, 149, 175, 0.15)',
									paddingTop: '8px',
								}}
							>
								<Table size='small'>
									<TableHead>
										<TableRow>
											{['Data', 'Hora Início', 'Hora Fim', 'Descrição'].map(label => (
												<TableCell
													key={label}
													sx={{ padding: '6px 16px' }}
												>
													<Typography sx={itineraryTableHeaderSx}>{label}</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{itineraryStops.map(stop => (
											<TableRow key={stop.id}>
												<TableCell sx={{ padding: '6px 16px' }}>
													<Typography sx={itineraryTableCellSx}>{stop.data}</Typography>
												</TableCell>
												<TableCell sx={{ padding: '6px 16px' }}>
													<Typography sx={itineraryTableCellSx}>{stop.horaInicio}</Typography>
												</TableCell>
												<TableCell sx={{ padding: '6px 16px' }}>
													<Typography sx={itineraryTableCellSx}>{stop.horaFim}</Typography>
												</TableCell>
												<TableCell sx={{ padding: '6px 16px' }}>
													<Typography sx={itineraryTableCellSx}>{stop.descricao}</Typography>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Box>
						</Box>

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '20px',
									lineHeight: 1.3,
									color: '#092056',
								}}
							>
								Despesas
							</Typography>
							<TextField
								label='Despesas do Motorista'
								size='small'
								value='Despesas do(s) Motorista(s) são por conta de Transol - Transportes e Turismo SA'
								sx={itineraryFieldSx}
								multiline
								minRows={1}
							/>
							<TextField
								label='Despesas s/ portagens'
								size='small'
								value='Portagens + IVA são por conta de Transol - Transportes e Turismo SA'
								sx={itineraryFieldSx}
								multiline
								minRows={1}
							/>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							padding: '8px 16px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={handleCloseItinerary}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '13px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
					</Box>
				</Box>
			</Dialog>

			<Drawer
				anchor='left'
				open={manageDrawerOpen}
				onClose={handleCloseManageDrawer}
				PaperProps={{ sx: manageDrawerPaperSx }}
				ModalProps={{ keepMounted: true }}
				sx={{
					zIndex: 1600,
					'& .MuiDrawer-paper': { zIndex: 1600 },
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
					<Box sx={manageDrawerHeaderSx}>
						<Typography sx={manageDrawerTitleSx}>{manageLabel}</Typography>
						<IconButton
							size='small'
							onClick={handleCloseManageDrawer}
							sx={manageDrawerCloseButtonSx}
							aria-label='fechar'
						>
							<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
						</IconButton>
					</Box>

					<Box
						sx={{
							flex: 1,
							overflow: 'auto',
							padding: '24px',
							display: 'flex',
							flexDirection: 'column',
							gap: 0,
						}}
					>
						<Box sx={manageDrawerSectionSx}>
							<ButtonBase
								sx={manageDrawerFilterHeaderButtonSx}
								onClick={() => setManageFiltersOpen(prev => !prev)}
								aria-expanded={manageFiltersOpen}
								aria-controls='manage-filters-content'
							>
								<Typography sx={manageDrawerSectionTitleSx}>Filtrar</Typography>
								<KeyboardArrowUpRoundedIcon
									sx={{
										...manageDrawerSectionCaretSx,
										transform: manageFiltersOpen ? 'rotate(0deg)' : 'rotate(180deg)',
									}}
								/>
							</ButtonBase>
							<Collapse
								in={manageFiltersOpen}
								unmountOnExit
								timeout={200}
								easing={{
									enter: 'cubic-bezier(0.33, 1, 0.68, 1)',
									exit: 'cubic-bezier(0.32, 0, 0.67, 0)',
								}}
							>
								<Box
									id='manage-filters-content'
									role='region'
									aria-label='Filtrar'
									sx={{
										display: 'grid',
										gap: '16px',
										paddingTop: '24px',
										paddingBottom: 0,
									}}
								>
									{resourcesTab === 0 ? (
										<>
											<TextField
												label='Viatura'
												select
												size='small'
												fullWidth
												value={vehicleFilter}
												onChange={event => setVehicleFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Todos'>Todos</MenuItem>
											</TextField>
											<TextField
												label={'Situação / Disponibilidade'}
												select
												size='small'
												fullWidth
												value={vehicleAvailabilityFilter}
												onChange={event => setVehicleAvailabilityFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Todos'>Todos</MenuItem>
											</TextField>
											<TextField
												label='Tipologia'
												select
												size='small'
												fullWidth
												value={vehicleTypeFilter}
												onChange={event => setVehicleTypeFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Todas'>Todas</MenuItem>
											</TextField>
											<TextField
												label={'Lotação'}
												select
												size='small'
												fullWidth
												value={vehicleCapacityFilter}
												onChange={event => setVehicleCapacityFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Todas'>Todas</MenuItem>
											</TextField>
										</>
									) : (
										<>
											<TextField
												label='Motorista'
												select
												size='small'
												fullWidth
												value={driverFilter}
												onChange={event => setDriverFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Todos'>Todos</MenuItem>
											</TextField>
											<TextField
												label={'Situação / Disponibilidade'}
												select
												size='small'
												fullWidth
												value={availabilityFilter}
												onChange={event => setAvailabilityFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Todos'>Todos</MenuItem>
											</TextField>
											<TextField
												label={'Seleção de Motoristas'}
												select
												size='small'
												fullWidth
												value={selectionFilter}
												onChange={event => setSelectionFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Casa e Externos'>Casa e Externos</MenuItem>
											</TextField>
											<TextField
												label='Zona'
												select
												size='small'
												fullWidth
												value={zoneFilter}
												onChange={event => setZoneFilter(event.target.value)}
												SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
												sx={manageDrawerFieldSx}
											>
												<MenuItem value='Todas'>Todas</MenuItem>
											</TextField>
										</>
									)}
								</Box>
							</Collapse>
						</Box>

						<Box sx={manageDrawerSectionCompactSx}>
							<ButtonBase
								sx={manageDrawerFilterHeaderButtonSx}
								onClick={() => setManageConsultOpen(prev => !prev)}
								aria-expanded={manageConsultOpen}
								aria-controls='manage-consult-content'
							>
								<Typography sx={manageDrawerSectionTitleSx}>Consultar</Typography>
								<KeyboardArrowUpRoundedIcon
									sx={{
										...manageDrawerSectionCaretSx,
										transform: manageConsultOpen ? 'rotate(0deg)' : 'rotate(180deg)',
									}}
								/>
							</ButtonBase>
							<Collapse
								in={manageConsultOpen}
								unmountOnExit
								timeout={200}
								easing={{
									enter: 'cubic-bezier(0.33, 1, 0.68, 1)',
									exit: 'cubic-bezier(0.32, 0, 0.67, 0)',
								}}
							>
								<Box
									id='manage-consult-content'
									role='region'
									aria-label='Consultar'
									sx={{
										...(resourcesTab === 0 ? manageDrawerListVehiclesSx : manageDrawerListSx),
										paddingTop: '8px',
									}}
								>
									{(resourcesTab === 0 ? manageDrawerConsultItemsVehicles : manageDrawerConsultItemsDrivers).map(
										item => (
											<ButtonBase
												key={item}
												onClick={() => handleConsultItemClick(item)}
												sx={resourcesTab === 0 ? manageDrawerListItemVehiclesSx : manageDrawerListItemSx}
											>
												<Typography sx={manageDrawerListTextSx}>{item}</Typography>
												<KeyboardArrowRightRoundedIcon
													sx={resourcesTab === 0 ? manageDrawerListIconVehiclesSx : manageDrawerListIconSx}
												/>
											</ButtonBase>
										),
									)}
								</Box>
							</Collapse>
						</Box>
					</Box>
				</Box>
			</Drawer>
			<DriversManagementDrawer
				open={manageDriversDrawerOpen}
				onClose={handleCloseManageDriversDrawer}
			/>
			<VehiclesManagementDrawer
				open={manageVehiclesDrawerOpen}
				onClose={handleCloseManageVehiclesDrawer}
			/>
		</>
	)
})

export default SchedulesServicePage
